/**
 * AddressCorrectionList - List container for address correction reviews
 *
 * SESSION 197: Address Correction Frontend Admin UI
 * SESSION_315: Updated to use AdminEmptyState/AdminLoadingState for consistent width
 * SESSION_352: Added responsive table/card layout (tables on desktop, cards on mobile)
 *
 * Provides paginated list of address corrections with loading/error states.
 * Uses table view on desktop (>=768px) and card view on mobile (<768px).
 */
import React from 'react';
import { Stack, Text, Alert, Pagination, Group } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertCircle, IconMapPin } from '@tabler/icons-react';
import { AddressCorrectionCard } from './AddressCorrectionCard';
import { AddressCorrectionTable } from './AddressCorrectionTable';
import { AdminEmptyState, AdminLoadingState } from '../shared';
import type { AddressCorrectionListProps } from './AddressCorrectionReviewSection.types';

/**
 * AddressCorrectionList Component
 *
 * Displays paginated list of address corrections.
 * SESSION_352: Uses table view on desktop (>=768px), card view on mobile (<768px)
 */
export const AddressCorrectionList: React.FC<AddressCorrectionListProps> = ({
  corrections,
  isLoading,
  error,
  page,
  totalPages,
  onPageChange,
  onApprove,
  onReject,
  actionsDisabled
}): React.ReactElement => {
  // SESSION_352: Responsive breakpoint - table on desktop, cards on mobile
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // Loading state
  if (isLoading) {
    return <AdminLoadingState message="Loading address corrections..." />;
  }

  // Error state
  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
        {error.message}
      </Alert>
    );
  }

  // Empty state
  if (corrections.length === 0) {
    return (
      <AdminEmptyState
        icon={<IconMapPin size={52} stroke={1.2} />}
        title="No address correction requests found"
        description="Address corrections from business owners will appear here for review"
      />
    );
  }

  return (
    <Stack gap="md">
      {/* SESSION_352: Table view for desktop, card view for mobile */}
      {isDesktop ? (
        <AddressCorrectionTable
          corrections={corrections}
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
          onApprove={onApprove}
          onReject={onReject}
          actionsDisabled={actionsDisabled}
        />
      ) : (
        /* Mobile: Card view */
        corrections.map((correction) => (
          <AddressCorrectionCard
            key={correction.requestId}
            correction={correction}
            onApprove={onApprove}
            onReject={onReject}
            actionsDisabled={actionsDisabled}
          />
        ))
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Stack gap="xs" align="center">
            <Pagination
              total={totalPages}
              value={page}
              onChange={onPageChange}
              withEdges
            />
            <Text size="sm" c="dimmed">
              Page {page} of {totalPages}
            </Text>
          </Stack>
        </Group>
      )}
    </Stack>
  );
};
